import React, { useCallback, useEffect, useState } from "react";
import {
  EstudioLaboratorio,
  EstudioPaciente,
  Evolucion,
  Paciente,
  Turno,
} from "../../interfaces";

import {
  IonButton,
  IonCard,
  IonCol,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonRow,
  IonSegment,
  IonSegmentButton,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import dayjs from "dayjs";
import {
  documentAttach,
  documentAttachOutline,
  documentText,
  documentTextOutline,
  peopleCircleOutline,
  personCircleOutline,
} from "ionicons/icons";
import EvolucionViewer from "../../components/EvolucionViewer/EvolucionViewer";
import FichaPaciente from "../../components/FichaPaciente/FichaPaciente";
import ModalEditorEvoluciones from "../../components/Modales/ModalEditorEvoluciones";
import TurnoViewer from "../../components/TurnoViewer/TurnoViewer";
import ViewerEstudiosPaciente from "../../components/ViewerEstudiosPaciente/ViewerEstudiosPaciente";
import { COLORS } from "../../constants/app";
import http from "../../hooks/httpAdapter";
import { useSession } from "../../hooks/SessionContext";
import useTituloPagina from "../../hooks/tituloPagina";
import { transformers } from "../../utils/transformers";

interface ContainerProps {}

const HistoriaClinica: React.FC<ContainerProps> = () => {
  const {
    turnoSeleccionado,
    pacienteCargado,
    setPacienteCargado,
    hcUL,
    cargandoSession,
    setSesionInvalida,
    mostrarNotificacion,
  } = useSession();
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );
  const [mostrarSoloMisEvoluciones, setMostrarSoloMisEvoluciones] =
    useState<boolean>(false);
  const [mostrarTreeview, setMostrarTreeview] = useState<string>("Evoluciones");
  const [evoluciones, setEvoluciones] = useState<Evolucion[]>([]);
  const [evolucionesListado, setEvolucionesListado] = useState<Evolucion[]>([]);
  const [estudiosLaboratorio, setEstudiosLaboratorio] = useState<
    EstudioLaboratorio[]
  >([]);
  const [estudiosPaciente, setEstudiosPaciente] = useState<EstudioPaciente[]>(
    [],
  );
  const [mostrarEvolucion, setMostrarEvolucion] = useState<Evolucion>();
  const [mostrarEvoSeleccionada, setMostrarEvoSeleccionada] =
    useState<boolean>(false);
  const [openModalAgregarEvolucion, setOpenModalAgregarEvolucion] =
    useState<boolean>(false);
  const [openModalEditarEvolucion, setOpenModalEditarEvolucion] =
    useState<boolean>(false);
  const [openMobileEvolucionViewer, setOpenMobileEvolucionViewer] =
    useState<boolean>(false);
  const [cerradaPorConfirmar, setCerradaPorConfirmar] =
    useState<boolean>(false);
  const [evolucionTurno, setEvolucionTurno] = useState<Evolucion>();

  useTituloPagina();

  const handleClickSeleccionarEvolucion = useCallback(
    (evolucionSel: Evolucion, abrirDetalleMobile: boolean = false) => {
      if (!openModalAgregarEvolucion && !openModalEditarEvolucion) {
        setMostrarEvolucion(evolucionSel);
        setMostrarEvoSeleccionada(true);
        if (isMobile && abrirDetalleMobile) {
          setOpenMobileEvolucionViewer(true);
        }
      }
    },
    [isMobile, openModalAgregarEvolucion, openModalEditarEvolucion],
  );

  const handleCloseMobileEvolucionViewer = useCallback(() => {
    setOpenMobileEvolucionViewer(false);
  }, []);

  const handleOpenEditorEvolucion = useCallback(
    (visible: boolean) => {
      if (visible && isMobile) {
        handleCloseMobileEvolucionViewer();
      }
      setOpenModalEditarEvolucion(visible);
    },
    [handleCloseMobileEvolucionViewer, isMobile],
  );

  const handleClickChangeEvolucion = useCallback(
    (sigPrev: "+" | "-") => {
      if (!openModalAgregarEvolucion && !openModalEditarEvolucion) {
        const currentIndex = evolucionesListado.findIndex(
          (evo: any) => evo === mostrarEvolucion,
        );

        if (currentIndex !== -1) {
          let newIndex = sigPrev === "+" ? currentIndex + 1 : currentIndex - 1;
          newIndex = Math.max(
            0,
            Math.min(newIndex, evolucionesListado.length - 1),
          );
          const nuevaEvolucionSeleccionada = evolucionesListado[newIndex];
          handleClickSeleccionarEvolucion(nuevaEvolucionSeleccionada);
        }
      }
    },
    [
      evolucionesListado,
      handleClickSeleccionarEvolucion,
      mostrarEvolucion,
      openModalAgregarEvolucion,
      openModalEditarEvolucion,
    ],
  );

  const traerEvolucionesPacientes = async (pacienteBuscar: Paciente) => {
    const config = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
    };

    if (pacienteBuscar?.codigo !== null) {
      try {
        const response = await http()
          .get(`Evoluciones/${pacienteBuscar?.codigo}`, config)

          .catch((e) => {
            if (e.response && e.response.status === 401) {
              setSesionInvalida(true);
              // history.push("/", { motivoRedireccion: "401" }); // Redirección en caso de error 401
            } else {
              console.error(e);
            }
          });
        let respEvoluciones: Evolucion[] = [];
        if (response.length > 0) {
          respEvoluciones = response;
        }

        setEvoluciones(respEvoluciones);
        setEvolucionesListado(respEvoluciones);
        handleClickSeleccionarEvolucion(respEvoluciones[0], false);

        // const generateRandomDate = (start: Date, end: Date) => {
        //   return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
        // };

        // const prestadores = [
        //   { prestadorCod: 1, prestadorNom: "Dr. Smith" },
        //   { prestadorCod: 2, prestadorNom: "Dr. Johnson" },
        //   { prestadorCod: 3, prestadorNom: "Dr. Brown" },
        //   { prestadorCod: 4, prestadorNom: "Dr. Taylor" },
        //   { prestadorCod: 5, prestadorNom: "Dr. Anderson" },
        //   { prestadorCod: 6, prestadorNom: "Dr. Thomas" },
        // ];

        // const exampleEvoluciones: Evolucion[] = Array.from({ length: 25 }, (_, index) => {
        //   const randomPrestador = prestadores[Math.floor(Math.random() * prestadores.length)];
        //   return {
        //     codigo: index + 1,
        //     fecha: generateRandomDate(new Date(Date.now() - 2 * 365 * 24 * 60 * 60 * 1000), new Date()).toISOString(),
        //     prestadorCod: randomPrestador.prestadorCod,
        //     prestadorNom: randomPrestador.prestadorNom,
        //     pacienteCod: pacienteBuscar.codigo || 0,
        //     motivoConsulta: `Motivo consulta ${index + 1}`,
        //     motivo: `Motivo ${index + 1}`,
        //     diagnosticoCod: index + 100,
        //     diagnosticoNom: `Diagnóstico ${index + 1}`,
        //     derivanteMat: `Matricula ${index + 1}`,
        //     derivanteNom: `Derivante ${index + 1}`,
        //   };
        // });

        // const evolucionesOrdenadas = exampleEvoluciones.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());

        // setEvoluciones(evolucionesOrdenadas);
        // setEvolucionesListado(evolucionesOrdenadas);
        // handleClickSeleccionarEvolucion(evolucionesOrdenadas[0]);
      } catch (e) {
        //@ts-ignore
        mostrarNotificacion(true, "Error: " + e, "rojo");
      }
    }
  };

  const traerLaboratoriosPacientes = async (pacienteBuscar: Paciente) => {
    const config = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
    };
    if (pacienteBuscar?.codigo !== null) {
      try {
        const response = await http()
          .get(
            `EstudiosLaboratorio/${pacienteBuscar?.codigo}`,

            config,
          )
          .catch((e) => {
            if (e.response && e.response.status === 401) {
              setSesionInvalida(true);
              // history.push("/", { motivoRedireccion: "401" }); // Redirección en caso de error 401
            } else {
              console.error(e);
            }
          });
        let respLaboratorio = [];
        if (response.length > 0) {
          respLaboratorio = response;
        }
        setEstudiosLaboratorio(respLaboratorio);
      } catch (e) {
        //@ts-ignore
        mostrarNotificacion(true, "Error: " + e, "rojo");
      }
    }
  };

  const traerEstudiosPacientes = async (pacienteBuscar: Paciente) => {
    const config = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
    };
    if (pacienteBuscar?.codigo !== null) {
      try {
        const response = await http()
          .get(
            `EstudiosPacientes/${pacienteBuscar?.codigo}`,

            config,
          )
          .catch((e) => {
            if (e.response && e.response.status === 401) {
              setSesionInvalida(true);
              // history.push("/", { motivoRedireccion: "401" }); // Redirección en caso de error 401
            } else {
              console.error(e);
            }
          });
        let respEstudio = [];
        if (response.length > 0) {
          respEstudio = response;
        }
        setEstudiosPaciente(respEstudio);
      } catch (e) {
        //@ts-ignore
        mostrarNotificacion(true, "Error: " + e, "rojo");
      }
    }
  };

  const CargarEvoluciones = async (pacienteCargado: Paciente) => {
    await traerEvolucionesPacientes(pacienteCargado);
    await traerEstudiosPacientes(pacienteCargado);
    await traerLaboratoriosPacientes(pacienteCargado);
  };

  const traerPaciente = async (turno?: Turno) => {
    let pacienteResponse: Paciente | null = pacienteCargado;
    try {
      const config = {
        headers: { Authorization: `Bearer ${hcUL?.token}` },
      };
      if (turno && (pacienteCargado === null || pacienteCargado.codigo === 0)) {
        let response = await http()
          .get(
            `pacientes/?documento=${turno?.pacienteDocumento}`,

            config,
          )
          .catch((e) => {
            if (e.response && e.response.status === 401) {
              setSesionInvalida(true);
              // history.push("/", { motivoRedireccion: "401" }); // Redirección en caso de error 401
            } else {
              console.error(e);
            }
          });
        if (response.length === 1) {
          pacienteResponse = transformers.toPaciente(response[0]);
          setPacienteCargado(pacienteResponse);
          await CargarEvoluciones(pacienteResponse);
        }
      } else {
        if (pacienteCargado !== null && pacienteCargado.codigo !== 0) {
          await CargarEvoluciones(pacienteCargado);
        } else {
          mostrarNotificacion(
            true,
            "No se encuentra el pacienteCargado indicado",
            COLORS.WARNING,
          );
        }
      }
    } catch (e) {
      mostrarNotificacion(true, "Error: " + e, COLORS.ERROR);
    }
  };

  const filtrarEvoluciones = useCallback(
    (filtrar: boolean) => {
      let listadoEvoluciones: Evolucion[] = evoluciones;

      if (filtrar) {
        listadoEvoluciones = listadoEvoluciones.filter(
          (evolucion) => evolucion.prestadorCod === hcUL?.prestadorCod,
        );
      }
      if (listadoEvoluciones.length > 0) {
        handleClickSeleccionarEvolucion(listadoEvoluciones[0], false);
      }
      setEvolucionesListado(listadoEvoluciones);
    },
    [evoluciones, handleClickSeleccionarEvolucion, hcUL?.prestadorCod],
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    filtrarEvoluciones(mostrarSoloMisEvoluciones);
  }, [mostrarSoloMisEvoluciones, filtrarEvoluciones]);

  useEffect(() => {
    if (!isMobile || mostrarTreeview !== "Evoluciones") {
      handleCloseMobileEvolucionViewer();
    }
  }, [handleCloseMobileEvolucionViewer, isMobile, mostrarTreeview]);

  useEffect(() => {
    if (cargandoSession === false) {
      if (pacienteCargado !== null && pacienteCargado.codigo !== 0) {
        let turnoTmp: Turno | null =
          turnoSeleccionado !== null &&
          turnoSeleccionado.paciente === pacienteCargado?.codigo
            ? turnoSeleccionado
            : null;
        if (turnoTmp !== null) {
          traerPaciente(turnoTmp);
        } else {
          CargarEvoluciones(pacienteCargado);
        }
      } else {
        setMostrarSoloMisEvoluciones(false);
        setMostrarTreeview("Evoluciones");
        setEvoluciones([]);
        setEvolucionesListado([]);
        setEstudiosLaboratorio([]);
        setEstudiosPaciente([]);
        setMostrarEvoSeleccionada(false);
        setOpenModalAgregarEvolucion(false);
        setOpenModalEditarEvolucion(false);
        setOpenMobileEvolucionViewer(false);
        setCerradaPorConfirmar(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pacienteCargado,
    turnoSeleccionado,
    openModalEditarEvolucion,
    cerradaPorConfirmar,
    cargandoSession,
  ]);

  useEffect(() => {
    let evoTurno: Evolucion = {
      codigo: 0,
      fecha: "",
      prestadorCod: 0,
      prestadorNom: "",
      pacienteCod: 0,
      motivoConsulta: "",
      motivo: "",
      diagnosticoCod: 0,
      diagnosticoNom: "",
      derivanteMat: "",
      derivanteNom: "",
    };

    evoluciones.forEach((evolucion: any) => {
      if (dayjs(evolucion.fecha).isSame(dayjs(turnoSeleccionado?.fecha)))
        evoTurno = evolucion;
    });

    setEvolucionTurno(evoTurno);
  }, [evoluciones, turnoSeleccionado?.fecha]);

  const renderEstudiosLaboratorio = () => {
    return estudiosLaboratorio.map((estudio: any) => (
      <IonItem className="fila ion-align-items-center" key={estudio.codigo}>
        <IonCol className="celda" size="3">
          <p>{dayjs(estudio.fecha).format("YYYY-MM-DD")}</p>
        </IonCol>
        <IonCol className="celda" size="9">
          <p>{estudio.prestadorNom}</p>
        </IonCol>
      </IonItem>
    ));
  };

  const renderEvolucionesPaciente = () => {
    return evolucionesListado.map((evolucion) => (
      <IonItem
        key={evolucion.codigo}
        button
        onClick={() => handleClickSeleccionarEvolucion(evolucion, true)}
        className={
          evolucion === mostrarEvolucion
            ? "selected fila ion-align-items-start"
            : "fila ion-align-items-start"
        }
      >
        <IonIcon
          ios={documentTextOutline}
          md={documentText}
          size="large"
          slot="start"
        />
        <IonLabel>
          <small>{dayjs(evolucion.fecha).format("YYYY-MM-DD")}</small>
          <h6>{evolucion.prestadorNom}</h6>
        </IonLabel>
      </IonItem>
    ));
  };

  const mostrarTurnoViewer =
    turnoSeleccionado !== null && turnoSeleccionado?.codigo !== -1;

  return (
    <>
      <IonCard>
        <FichaPaciente recargarPagina={true} mostrarBuscarOtro={false} />
      </IonCard>
      {pacienteCargado !== null && pacienteCargado?.codigo !== 0 && (
        <>
          {!isMobile && mostrarTurnoViewer && (
            <TurnoViewer evolucionTurno={evolucionTurno} />
          )}

          <IonCard>
            <IonSegment
              value={mostrarTreeview}
              onIonChange={(e: any) => setMostrarTreeview(e.detail.value)}
            >
              <IonSegmentButton value="Evoluciones">
                <IonLabel>Evoluciones</IonLabel>
              </IonSegmentButton>
              {/* <IonSegmentButton value="Laboratorio">
                <IonLabel>Laboratorio</IonLabel>
              </IonSegmentButton> */}
              <IonSegmentButton value="Estudios">
                <IonLabel>Estudios</IonLabel>
              </IonSegmentButton>
            </IonSegment>

            <IonRow
              className="ion-align-items-start"
              style={{ marginTop: "1rem" }}
            >
              {mostrarTreeview === "Evoluciones" && (
                <>
                  <IonCol sizeXs="12" sizeMd="4">
                    <IonList>
                      <IonItem
                        button
                        onClick={() => setOpenModalAgregarEvolucion(true)}
                      >
                        <IonText>Agregar nueva evolucion</IonText>
                        <IonIcon
                          slot="end"
                          md={documentAttach}
                          ios={documentAttachOutline}
                        />
                      </IonItem>
                      {evoluciones.length > 0 && (
                        <div style={{ maxHeight: "350px", overflowY: "auto" }}>
                          <IonItem
                            button
                            onClick={() =>
                              setMostrarSoloMisEvoluciones(
                                !mostrarSoloMisEvoluciones,
                              )
                            }
                          >
                            <IonText>
                              {!mostrarSoloMisEvoluciones
                                ? "Todas las evoluciones"
                                : "Solo mis evoluciones"}
                            </IonText>
                            <IonIcon
                              slot="end"
                              icon={
                                !mostrarSoloMisEvoluciones
                                  ? peopleCircleOutline
                                  : personCircleOutline
                              }
                              size="large"
                            />
                          </IonItem>
                          {renderEvolucionesPaciente()}
                        </div>
                      )}
                    </IonList>
                  </IonCol>
                  <IonCol sizeXs="12" sizeMd="8">
                    {!isMobile &&
                      evoluciones.length > 0 &&
                      mostrarEvoSeleccionada && (
                        <EvolucionViewer
                          evolucionesDisponibles={evolucionesListado}
                          handleClickChangeEvolucion={
                            handleClickChangeEvolucion
                          }
                          evolucionSel={mostrarEvolucion}
                          setVisible={setMostrarEvoSeleccionada}
                          setMostrarEditorEvolucion={handleOpenEditorEvolucion}
                        />
                      )}
                  </IonCol>
                </>
              )}

              {/* {mostrarTreeview === "Estudios" && (
                <IonCol sizeXs="12">
                  {estudiosPaciente.length > 0 ? (
                    <IonList>
                      <IonItem color="light">
                        <IonText>
                          <h5>Estudios del Paciente</h5>
                        </IonText>
                      </IonItem>
                      <ViewerEstudiosPaciente paciente={pacienteCargado} />
                    </IonList>
                  ) : (
                    <IonItem>
                      <IonText>No hay estudios registrados</IonText>
                    </IonItem>
                  )}
                </IonCol>
              )}

              {mostrarTreeview === "Laboratorio" && (
                <IonCol sizeXs="12">
                  {estudiosLaboratorio.length > 0 ? (
                    <IonList>
                      <IonItem color="light">
                        <IonText>
                          <h5>Estudios de Laboratorio</h5>
                        </IonText>
                      </IonItem>
                      {renderEstudiosLaboratorio()}
                    </IonList>
                  ) : (
                    <IonItem>
                      <IonText>No hay estudios de laboratorio registrados</IonText>
                    </IonItem>
                  )}
                </IonCol>
              )} */}
              {(mostrarTreeview === "Estudios" ||
                mostrarTreeview === "Laboratorio") && (
                <ViewerEstudiosPaciente paciente={pacienteCargado} />
              )}
            </IonRow>
          </IonCard>

          {/* Bloque de turno oculto temporalmente en mobile por pedido del cliente. */}
          {/* {isMobile && mostrarTurnoViewer && <TurnoViewer evolucionTurno={evolucionTurno} />} */}
        </>
      )}
      {isMobile && evoluciones.length > 0 && mostrarEvoSeleccionada && (
        <IonModal
          isOpen={openMobileEvolucionViewer}
          onDidDismiss={handleCloseMobileEvolucionViewer}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Detalle de Evolución</IonTitle>
              <IonButton
                slot="end"
                fill="clear"
                onClick={handleCloseMobileEvolucionViewer}
              >
                Cerrar
              </IonButton>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <EvolucionViewer
              evolucionesDisponibles={evolucionesListado}
              handleClickChangeEvolucion={handleClickChangeEvolucion}
              evolucionSel={mostrarEvolucion}
              mostrarFecha={true}
              setVisible={setMostrarEvoSeleccionada}
              setMostrarEditorEvolucion={handleOpenEditorEvolucion}
            />
          </IonContent>
        </IonModal>
      )}
      {hcUL !== null &&
        hcUL.codigo !== 0 &&
        pacienteCargado !== null &&
        pacienteCargado.codigo !== 0 && (
          <>
            {openModalEditarEvolucion && (
              <ModalEditorEvoluciones
                abrirModal={openModalEditarEvolucion}
                setAbrirModal={setOpenModalEditarEvolucion}
                setCerradaPorConfirmar={setCerradaPorConfirmar}
                evolucionSel={mostrarEvolucion}
              />
            )}
            {openModalAgregarEvolucion && (
              <ModalEditorEvoluciones
                evolucionAnt={evolucionesListado[0]}
                abrirModal={openModalAgregarEvolucion}
                setAbrirModal={setOpenModalAgregarEvolucion}
                setCerradaPorConfirmar={setCerradaPorConfirmar}
              />
            )}
          </>
        )}
    </>
  );
};

export default HistoriaClinica;
